import { ExecutionController, ExecutionState } from './ExecutionController'
import { Variable, LoopContext } from '../types'
import { MonacoEditor } from '../editor/MonacoEditor'
import { VariablePanel } from './VariablePanel'
import { LoopConditionPanel } from './LoopConditionPanel'
import { LoopFlowVisualizer } from '../visualization/LoopFlowVisualizer'
import { ControlButtons } from './ControlButtons'
import { StatusIndicator } from './StatusIndicator'
import { SpeedController } from './SpeedController'
import { SpeedVisualizer } from './SpeedVisualizer'
import { ErrorDisplayPanel } from './ErrorDisplayPanel'
import { ErrorHighlighter } from './ErrorHighlighter'
import { SampleCodePanel } from './SampleCodePanel'
import { TutorialOverlay } from './TutorialOverlay'
import { HelpDialog } from './HelpDialog'
import { TabManager } from './TabManager'
import { RouteInfo } from '../router'

export class UIManager {
  private executionController: ExecutionController
  private monacoEditor: MonacoEditor | null = null
  private variablePanel: VariablePanel | null = null
  private loopConditionPanel: LoopConditionPanel | null = null
  private loopFlowVisualizer: LoopFlowVisualizer | null = null
  private controlButtons: ControlButtons
  private statusIndicator: StatusIndicator
  private speedController: SpeedController
  private speedVisualizer: SpeedVisualizer
  private errorDisplayPanel: ErrorDisplayPanel
  private errorHighlighter: ErrorHighlighter
  private sampleCodePanel: SampleCodePanel
  private tutorialOverlay: TutorialOverlay
  private helpDialog: HelpDialog
  private tabManager: TabManager
  private breakpoints: Set<number> = new Set()
  
  // DOM要素
  private logContent: HTMLDivElement
  private variablesList: HTMLDivElement
  private editorContainer: HTMLElement
  private loopConditionContainer: HTMLDivElement
  private loopVisualizerCanvas: HTMLCanvasElement
  
  // 現在のハイライト行
  private currentHighlightLine: number = 0
  
  // 現在のコード
  private currentCode: string = ''
  
  // ルート情報
  private routeInfo: RouteInfo | null = null
  
  constructor(routeInfo?: RouteInfo) {
    this.routeInfo = routeInfo || null
    // DOM要素を取得
    this.logContent = document.getElementById('execution-log') as HTMLDivElement
    this.variablesList = document.getElementById('variables-list') as HTMLDivElement
    this.editorContainer = document.getElementById('monaco-editor') as HTMLElement
    this.loopConditionContainer = document.getElementById('loop-condition') as HTMLDivElement
    this.loopVisualizerCanvas = document.getElementById('loop-visualizer') as HTMLCanvasElement
    
    // コントロールボタンを初期化
    this.controlButtons = new ControlButtons()
    
    // ステータスインジケーターを初期化
    this.statusIndicator = new StatusIndicator()
    
    // 速度コントローラーを初期化
    this.speedController = new SpeedController()
    
    // 速度ビジュアライザーを初期化
    this.speedVisualizer = new SpeedVisualizer()
    
    // エラー表示システムを初期化
    this.errorDisplayPanel = new ErrorDisplayPanel()
    this.errorHighlighter = new ErrorHighlighter()
    ErrorHighlighter.setupStyles()
    
    // ユーザーガイダンス機能を初期化
    this.sampleCodePanel = new SampleCodePanel(this.routeInfo)
    this.tutorialOverlay = new TutorialOverlay()
    this.helpDialog = new HelpDialog()
    
    // タブマネージャーを初期化
    this.tabManager = new TabManager()
    
    // サンプルコード選択時のコールバック
    this.sampleCodePanel.setOnSelectCallback((code) => {
      if (this.monacoEditor) {
        this.monacoEditor.setValue(code)
      }
    })
    
    // チュートリアルを初回起動時に表示
    if (!TutorialOverlay.hasBeenShown()) {
      setTimeout(() => {
        this.tutorialOverlay.start()
      }, 500)
    }
    
    // ExecutionControllerを作成
    this.executionController = new ExecutionController({
      onVariableUpdate: (variables) => this.updateVariableDisplay(variables),
      onLineHighlight: (line) => this.highlightLine(line),
      onLog: (message, type) => this.addLog(message, type),
      onStateChange: (state) => this.updateButtonStates(state),
      onError: (errors) => this.setErrors(errors),
      onLoopStackUpdate: (loopStack) => this.updateLoopConditionDisplay(loopStack),
    })
    
    // イベントリスナーを設定
    this.setupEventListeners()
    
    // 初期ログ
    this.addLog('アプリケーションが起動しました', 'info')
    
    // 変数パネルを初期化
    this.initializeVariablePanel()
    
    // ループ条件パネルを初期化
    this.initializeLoopConditionPanel()
    
    // ループフロー可視化を初期化
    this.initializeLoopFlowVisualizer()
    
    // タブ切り替え時のコールバック
    this.tabManager.setOnTabChangeCallback((tabId) => {
      if (tabId === 'flow' && this.loopFlowVisualizer) {
        // キャンバスのリサイズをトリガー
        setTimeout(() => {
          const canvas = this.loopVisualizerCanvas
          const rect = canvas.getBoundingClientRect()
          canvas.width = rect.width
          canvas.height = rect.height
          this.loopFlowVisualizer?.render()
        }, 100)
      }
    })
  }
  
  /**
   * コードを設定
   */
  setCode(code: string): void {
    this.currentCode = code
    this.executionController.setCode(code)
    this.initializeMonacoEditor(code)
  }
  
  /**
   * Monaco Editorを初期化
   */
  private initializeMonacoEditor(code: string): void {
    if (this.monacoEditor) {
      this.monacoEditor.setValue(code)
      return
    }
    
    // Monaco Editorを作成
    this.monacoEditor = new MonacoEditor({
      container: this.editorContainer,
      initialValue: code,
      onChange: (value) => {
        this.currentCode = value
        this.executionController.setCode(value)
        // コードが変更されたら実行状態をリセット
        if (this.executionController.getState() !== 'idle') {
          this.executionController.reset()
        }
      },
      onLineChange: () => {
        // カーソル位置の変更を追跡（必要に応じて使用）
      },
      onBreakpointChange: (breakpoints) => {
        this.breakpoints = new Set(breakpoints)
        this.addLog(`ブレークポイント: ${breakpoints.join(', ')}`, 'info')
      },
    })
  }
  
  /**
   * 行をハイライト
   */
  private highlightLine(line: number): void {
    this.currentHighlightLine = line
    
    // Monaco Editorでハイライト
    if (this.monacoEditor) {
      this.monacoEditor.highlightLine(line)
    }
    
    // ブレークポイントチェック
    if (this.breakpoints.has(line) && this.executionController.getState() === 'running') {
      this.executionController.pause()
      this.addLog(`ブレークポイントに到達: 行 ${line}`, 'info')
    }
  }
  
  /**
   * 変数表示を更新
   */
  private updateVariableDisplay(variables: Map<string, Variable>): void {
    if (this.variablePanel) {
      this.variablePanel.updateVariables(variables)
    }
  }
  
  /**
   * ログを追加
   */
  private addLog(message: string, type: 'info' | 'error' | 'success' = 'info'): void {
    const className = type === 'error' ? 'log-entry--error' : 
                     type === 'success' ? 'log-entry--success' : ''
    this.logContent.innerHTML += `<div class="log-entry ${className}">${message}</div>`
    this.logContent.scrollTop = this.logContent.scrollHeight
  }
  
  /**
   * ボタンの状態を更新
   */
  private updateButtonStates(state: ExecutionState): void {
    this.controlButtons.updateState(state)
    
    // ステータスインジケーターを更新
    const stepCount = this.executionController.getStepCount()
    this.statusIndicator.updateStatus(state, stepCount)
  }
  
  /**
   * ループフロー可視化を初期化
   */
  private initializeLoopFlowVisualizer(): void {
    this.loopFlowVisualizer = new LoopFlowVisualizer({
      canvas: this.loopVisualizerCanvas,
    })
  }
  
  
  /**
   * イベントリスナーを設定
   */
  private setupEventListeners(): void {
    // コントロールボタンのイベントを設定
    this.controlButtons.setupEventListeners({
      onRun: () => {
        // エディターの値を最新に更新
        if (this.monacoEditor) {
          const code = this.monacoEditor.getValue()
          if (code !== this.currentCode) {
            this.executionController.setCode(code)
            this.currentCode = code
          }
        }
        
        const speed = this.speedController.getSpeed()
        this.executionController.run(speed)
      },
      onPause: () => {
        this.executionController.pause()
      },
      onStep: () => {
        // エディターの値を最新に更新
        if (this.monacoEditor) {
          const code = this.monacoEditor.getValue()
          if (code !== this.currentCode) {
            this.executionController.setCode(code)
            this.currentCode = code
          }
        }
        
        this.executionController.step()
      },
      onReset: () => {
        this.executionController.reset()
        // エラーパネルをクリア
        this.errorDisplayPanel.clearErrors()
        // エラーハイライトをクリア
        if (this.monacoEditor) {
          const editor = (this.monacoEditor as any).editor
          if (editor) {
            this.errorHighlighter.clearHighlights(editor)
          }
        }
        // ループフロー可視化の履歴をクリア
        if (this.loopFlowVisualizer) {
          this.loopFlowVisualizer.clearHistory()
        }
        // ループ条件パネルの履歴をクリア
        if (this.loopConditionPanel) {
          this.loopConditionPanel.clearHistory()
        }
      }
    })
    
    // 速度コントローラーのイベントを設定
    this.speedController.setOnSpeedChange((speed) => {
      // 実行中の場合は速度を更新
      if (this.executionController.getState() === 'running') {
        this.executionController.setSpeed(speed)
      }
      // 速度ビジュアライザーを更新
      this.speedVisualizer.updateSpeed(speed)
    })
  }
  
  /**
   * エラーを表示
   */
  setErrors(errors: Array<{ line: number; message: string }>): void {
    // Monaco Editorにエラーマーカーを設定
    if (this.monacoEditor) {
      this.monacoEditor.setErrors(errors)
      
      // エラーハイライトを追加
      const editor = (this.monacoEditor as any).editor
      if (editor) {
        this.errorHighlighter.highlightErrors(editor, errors)
      }
    }
    
    // エラーパネルに表示
    this.errorDisplayPanel.showErrors(errors.map(error => ({
      line: error.line,
      message: error.message,
      type: 'syntax' as const
    })))
    
    // エラーがある場合は実行を停止
    if (errors.length > 0 && this.executionController.getState() === 'running') {
      this.executionController.pause()
    }
  }
  
  /**
   * 変数パネルを初朞化
   */
  private initializeVariablePanel(): void {
    this.variablePanel = new VariablePanel({
      container: this.variablesList,
      onVariableHover: () => {
        // 変数ホバー時の処理（将来的に実装）
      },
    })
  }
  
  /**
   * ループ条件パネルを初朞化
   */
  private initializeLoopConditionPanel(): void {
    this.loopConditionPanel = new LoopConditionPanel({
      container: this.loopConditionContainer,
    })
  }
  
  /**
   * ループ条件表示を更新
   */
  private updateLoopConditionDisplay(loopStack: LoopContext[]): void {
    // ディープコピーを作成して、variablesAfterが正しく伝達されるようにする
    const loopStackCopy = loopStack.map(loop => ({
      ...loop,
      variables: loop.variables ? [...loop.variables] : [],
      variablesAfter: loop.variablesAfter ? [...loop.variablesAfter] : undefined
    }))
    
    // デバッグログ削除
    
    if (this.loopConditionPanel) {
      this.loopConditionPanel.updateLoopStack(loopStackCopy)
    }
    if (this.loopFlowVisualizer) {
      this.loopFlowVisualizer.updateLoopStack(loopStackCopy)
    }
  }
  
  /**
   * クリーンアップ
   */
  dispose(): void {
    if (this.monacoEditor) {
      this.monacoEditor.dispose()
    }
    if (this.variablePanel) {
      this.variablePanel.dispose()
    }
    if (this.loopConditionPanel) {
      this.loopConditionPanel.dispose()
    }
    if (this.loopFlowVisualizer) {
      this.loopFlowVisualizer.dispose()
    }
    if (this.speedVisualizer) {
      this.speedVisualizer.dispose()
    }
    if (this.tabManager) {
      this.tabManager.dispose()
    }
  }
}